import React, { useEffect, useState } from "react";
import { getFirestoreApi } from "../firebase";
import { track } from "../lib/analytics";
import { cleanField, cleanEmail, cleanPhone, isHoneypotTripped } from "../lib/formSecurity";
import { useLocale, useL } from "../i18n/LocaleContext";
import { resolveEn } from "../i18n/locale";
import "./TripRegistrationModal.css";

/* ─────────────────────────────────────────────────────────────
   TripRegistrationModal

   Supports two flows via the `mode` prop:

     mode="fullBooking"   (default — UK camp)
        → chooser:
            Option 1 — Register Interest (lead form)
            Option 2 — Pay Refundable Deposit (registration + payment)

     mode="earlyAccess"   (Dubai camp)
        → goes straight to the Register Now form

   Firestore collections written:
     - "tripInterestLeads"          (Option 1)
     - "tripDepositRegistrations"   (Option 2)
     - "earlyAccessLeads"           (Dubai camp registration list)

   Payment integration is intentionally NOT hardcoded. The deposit
   form prepares a clean payload and calls `runPayment(...)` — see
   the marked block below to wire Stripe / Telr / PayTabs /
   Checkout.com / Network International.

   LANGUAGES (see I18N.md)
   This component receives the RAW trip record from the pages, so it
   resolves display copy itself with `useL` and keeps the raw record
   for the payloads. Everything that leaves the browser stays English:
   `resolveEn(...)` on every trip-derived value written to Firestore or
   passed to the payment provider, because firestore.rules pins the
   exact fields and shapes each form sends. No `locale` field, and no
   new field of any kind, is ever added to a payload.
   ─────────────────────────────────────────────────────────────*/

/* Preferred contact method. `value` is what Firestore stores and must
   stay English in every language (the rules cap it at 30 chars and the
   owner reads it in the console); `label` is what the parent sees. */
const CONTACT_OPTIONS = [
  { value: "WhatsApp", label: { en: "WhatsApp", ar: "واتساب",             ru: "WhatsApp" } },
  { value: "Call",     label: { en: "Call",     ar: "مكالمة",             ru: "Звонок" } },
  { value: "Email",    label: { en: "Email",    ar: "البريد الإلكتروني", ru: "Email" } },
];

const inputStyle = {
  width: "100%",
  borderRadius: 10,
  border: "2px solid rgba(11,22,56,0.15)",
  padding: "12px 14px",
  fontSize: 14.5,
  fontFamily: "inherit",
  color: "var(--navy)",
  background: "var(--warm)",
  outline: "none",
  transition: "border-color 0.2s",
  appearance: "none",
  boxSizing: "border-box",
};
const labelStyle = {
  display: "block",
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: 0.8,
  textTransform: "uppercase",
  color: "rgba(11,22,56,0.55)",
  marginBottom: 6,
};
const focus = (e) => { e.target.style.borderColor = "var(--navy)"; };
const blur  = (e) => { e.target.style.borderColor = "rgba(11,22,56,0.15)"; };

/* Money is formatted with the en-AE locale in all three languages on
   purpose: Western digits (0-9) are the UAE standard and ar-AE would
   render Arabic-Indic digits (٠١٢). Wrap the result in <bdi> wherever
   it sits next to Arabic text. */
function formatMoney(amount, currency) {
  try {
    return new Intl.NumberFormat("en-AE", {
      style: "currency",
      currency: currency || "AED",
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `${currency || "AED"} ${amount}`;
  }
}

/* <option> can only carry plain text, so <bdi> is not available there.
   The Unicode isolate pair does the same job for a price sitting in an
   Arabic option label. */
const isolate = (s) => `⁦${s}⁩`;

/* Fallback trip name when a page opens the modal without a record. */
const CAMP_FALLBACK = { en: "Kidsology camp", ar: "مخيم Kidsology", ru: "лагерь Kidsology" };

const COPY = {
  dialogLabel:   { en: "Trip registration",    ar: "التسجيل في الرحلة", ru: "Регистрация на поездку" },
  closeRegister: { en: "Close registration",   ar: "إغلاق التسجيل",     ru: "Закрыть регистрацию" },
  back:          { en: "← Back",               ar: "→ رجوع",            ru: "← Назад" },
  backLabel:     { en: "Back",                 ar: "رجوع",              ru: "Назад" },
  close:         { en: "Close",                ar: "إغلاق",             ru: "Закрыть" },
  tryAgain:      { en: "Try again",            ar: "حاول مرة أخرى",     ru: "Попробовать снова" },

  /* Shared field labels */
  parentName:  { en: "Parent full name",           ar: "الاسم الكامل لولي الأمر", ru: "Полное имя родителя" },
  childName:   { en: "Child full name",            ar: "الاسم الكامل للطفل",      ru: "Полное имя ребёнка" },
  childAge:    { en: "Child age",                  ar: "عمر الطفل",               ru: "Возраст ребёнка" },
  phone:       { en: "Phone number",               ar: "رقم الهاتف",              ru: "Номер телефона" },
  email:       { en: "Email address",               ar: "البريد الإلكتروني",       ru: "Email" },
  contactPref: { en: "Preferred contact method",   ar: "طريقة التواصل المفضلة",   ru: "Предпочтительный способ связи" },
  messageField:{ en: "Message or question",        ar: "رسالة أو سؤال",           ru: "Сообщение или вопрос" },

  /* Shared errors */
  checkDetails: {
    en: "Please check your details and try again.",
    ar: "تأكد من بياناتك وحاول مرة أخرى.",
    ru: "Проверьте ваши данные и попробуйте снова.",
  },
  paymentFailed: {
    en: "Payment was not completed. Please try again or contact our team for support.",
    ar: "لم تكتمل عملية الدفع. حاول مرة أخرى أو تواصل مع فريقنا للمساعدة.",
    ru: "Платёж не завершён. Попробуйте снова или обратитесь к нашей команде за помощью.",
  },
};

export default function TripRegistrationModal({
  open,
  onClose,
  trip,
  mode = "fullBooking", // "fullBooking" | "earlyAccess"
  defaultTrack = "",    // "" | "football" | "jiujitsu" — preselects an
                        // Elite Season Prep track and jumps straight to
                        // the deposit view so the bumped total fee shows
                        // immediately.
}) {
  const { t } = useLocale();
  // view depends on mode
  //  fullBooking: "chooser" | "info" | "deposit" | "info-done" | "deposit-done" | "error"
  //  earlyAccess: "early"   | "early-done"  | "error"
  const [view, setView] = useState(
    mode === "earlyAccess" ? "early" : (defaultTrack ? "deposit" : "chooser")
  );
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    /* Floating buttons (Kido, Register Now, page CTAs) hide while the
       form is open: on phones they sat over both ends of the submit
       button (SiteHeader.css, next to the menu's own rule). */
    document.body.classList.add("trm-open");
    return () => {
      document.body.style.overflow = prev;
      document.body.classList.remove("trm-open");
    };
  }, [open]);

  useEffect(() => {
    if (open) {
      setView(
        mode === "earlyAccess"
          ? "early"
          : (defaultTrack ? "deposit" : "chooser")
      );
      setErrorMsg("");
    }
  }, [open, mode, defaultTrack]);

  if (!open) return null;

  const baseFee = trip?.totalFee || 0;
  const currency = trip?.currency || "AED";

  return (
    <div
      className="trm-overlay"
      role="dialog"
      aria-modal="true"
      aria-label={t(COPY.dialogLabel)}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="trm-sheet">
        <button
          className="trm-close"
          onClick={onClose}
          aria-label={t(COPY.closeRegister)}
        >×</button>

        {/* ── Full booking flow ── */}
        {view === "chooser" && (
          <Chooser
            trip={trip}
            onPick={(next) => setView(next)}
          />
        )}

        {view === "info" && (
          <RequestInfoForm
            trip={trip}
            onBack={() => setView("chooser")}
            onDone={() => setView("info-done")}
            onError={(msg) => { setErrorMsg(msg); setView("error"); }}
          />
        )}

        {view === "deposit" && (
          <DepositForm
            trip={trip}
            baseFee={baseFee}
            currency={currency}
            defaultTrack={defaultTrack}
            onBack={() => setView("chooser")}
            onDone={() => setView("deposit-done")}
            onError={(msg) => { setErrorMsg(msg); setView("error"); }}
          />
        )}

        {/* ── Dubai registration flow ── */}
        {view === "early" && (
          <EarlyAccessForm
            trip={trip}
            onDone={() => setView("early-done")}
            onError={(msg) => { setErrorMsg(msg); setView("error"); }}
          />
        )}

        {/* ── Confirmations ── */}
        {view === "info-done" && (
          <Confirmation
            title={t({ en: "Thank you.", ar: "شكراً لك.", ru: "Спасибо." })}
            body={t({
              en: "Your interest has been submitted. Our team will contact you with full camp details, pricing, availability, and next steps.",
              ar: "تم إرسال طلبك. سيتواصل معك فريقنا بتفاصيل المخيم كاملة، والسعر، والأماكن المتاحة، والخطوات التالية.",
              ru: "Ваша заявка отправлена. Наша команда свяжется с вами и расскажет все детали лагеря, цену, наличие мест и следующие шаги.",
            })}
            onClose={onClose}
          />
        )}

        {view === "deposit-done" && (
          <Confirmation
            title={t({ en: "Spot reserved.", ar: "تم حفظ المقعد.", ru: "Место забронировано." })}
            body={t({
              en: "Your refundable deposit has been received and your child's spot has been reserved. Our team will contact you with the next steps.",
              ar: "استلمنا الدفعة المستردة وتم حفظ مقعد طفلك. سيتواصل معك فريقنا بالخطوات التالية.",
              ru: "Возвращаемый депозит получен, место для вашего ребёнка забронировано. Наша команда свяжется с вами и расскажет о следующих шагах.",
            })}
            onClose={onClose}
          />
        )}

        {view === "early-done" && (
          <Confirmation
            title={t({ en: "You're on the list.", ar: "أنت على القائمة.", ru: "Вы в списке." })}
            body={t({
              en: "You are on the Dubai camp list. The Kidsology team will contact you with the full camp details, pricing, and next steps.",
              ar: "أنت الآن على قائمة مخيم دبي. سيتواصل معك فريق Kidsology بتفاصيل المخيم كاملة، والسعر، والخطوات التالية.",
              ru: "Вы в списке зимнего лагеря в Дубае. Команда Kidsology свяжется с вами и расскажет все детали лагеря, цену и следующие шаги.",
            })}
            onClose={onClose}
          />
        )}

        {view === "error" && (
          <ErrorView
            message={errorMsg || t(COPY.paymentFailed)}
            onRetry={() => setView(mode === "earlyAccess" ? "early" : "chooser")}
            onClose={onClose}
          />
        )}
      </div>
    </div>
  );
}

/* ─── Chooser (UK Book Now) ───────────────────────────────── */
function Chooser({ trip, onPick }) {
  const { t } = useLocale();
  const tripL = useL(trip);
  const tripName = tripL?.name || t(CAMP_FALLBACK);

  return (
    <div className="trm-content">
      <p className="trm-eyebrow">
        {t({ en: "Register for", ar: "التسجيل في", ru: "Регистрация:" })}{" "}
        {tripName}
      </p>
      <h2 className="trm-title">
        {t({
          en: "Secure Your Child's Spot",
          ar: "احفظ مقعد طفلك",
          ru: "Забронируйте место для ребёнка",
        })}
      </h2>
      <p className="trm-sub">
        {t({
          en: "Choose how you would like to continue.",
          ar: "اختر الطريقة التي تفضل المتابعة بها.",
          ru: "Выберите, как вы хотите продолжить.",
        })}
      </p>
      <p className="trm-sub" style={{ marginTop: -10 }}>
        {t({
          en: "You can either register your interest and speak with our team first, or pay a fully refundable deposit to reserve your child's place and begin the registration process directly.",
          ar: "يمكنك تسجيل اهتمامك والتحدث مع فريقنا أولاً، أو دفع دفعة مستردة بالكامل لحفظ مقعد طفلك والبدء في إجراءات التسجيل مباشرة.",
          ru: "Вы можете оставить заявку и сначала поговорить с нашей командой или внести полностью возвращаемый депозит, чтобы забронировать место для ребёнка и сразу начать регистрацию.",
        })}
      </p>

      <div className="trm-options">
        <button
          className="trm-option"
          onClick={() => onPick("info")}
          type="button"
        >
          <div className="trm-option-head">
            <span className="trm-option-num">01</span>
            <span className="trm-option-name">
              {t({ en: "Register Interest", ar: "تسجيل الاهتمام", ru: "Оставить заявку" })}
            </span>
          </div>
          <p className="trm-option-desc">
            {t({
              en: "Leave your details and our team will contact you with full camp information, pricing, availability, and next steps.",
              ar: "اترك بياناتك وسيتواصل معك فريقنا بمعلومات المخيم كاملة، والسعر، والأماكن المتاحة، والخطوات التالية.",
              ru: "Оставьте свои данные, и наша команда свяжется с вами: полная информация о лагере, цена, наличие мест и следующие шаги.",
            })}
          </p>
          <span className="trm-option-cta">
            {t({ en: "Submit interest →", ar: "أرسل اهتمامك ←", ru: "Отправить заявку →" })}
          </span>
        </button>

        <button
          className="trm-option trm-option--primary"
          onClick={() => onPick("deposit")}
          type="button"
        >
          <div className="trm-option-head">
            <span className="trm-option-num">02</span>
            <span className="trm-option-name">
              {t({
                en: "Pay Refundable Deposit",
                ar: "دفع دفعة مستردة",
                ru: "Внести возвращаемый депозит",
              })}
            </span>
          </div>
          <p className="trm-option-desc">
            {t({
              en: "Pay a fully refundable deposit to reserve your child's place and begin the registration process directly. Our team will contact you to complete the next steps.",
              ar: "ادفع دفعة مستردة بالكامل لحفظ مقعد طفلك والبدء في إجراءات التسجيل مباشرة. سيتواصل معك فريقنا لإكمال الخطوات التالية.",
              ru: "Внесите полностью возвращаемый депозит, чтобы забронировать место для ребёнка и сразу начать регистрацию. Наша команда свяжется с вами для следующих шагов.",
            })}
          </p>
          <span className="trm-option-cta">
            {t({ en: "Pay deposit →", ar: "ادفع الدفعة ←", ru: "Внести депозит →" })}
          </span>
        </button>
      </div>
    </div>
  );
}

/* ─── Option 1 — Register Interest ────────────────────────── */
function RequestInfoForm({ trip, onBack, onDone, onError }) {
  const { t } = useLocale();
  const [form, setForm] = useState({
    parentName: "",
    childAge: "",
    phone: "",
    email: "",
    preferredContact: "WhatsApp",
    message: "",
    website: "",
  });
  const [status, setStatus] = useState("idle");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (isHoneypotTripped(form.website)) { onDone(); return; }
    const parentName = cleanField(form.parentName, 80);
    const childAge   = cleanField(form.childAge, 40);
    const phone      = cleanPhone(form.phone);
    const email      = cleanEmail(form.email);
    if (!parentName || !childAge || !phone.valid || !email.valid) {
      onError(t(COPY.checkDetails));
      return;
    }
    setStatus("saving");
    try {
      const { db, collection, addDoc, serverTimestamp } = await getFirestoreApi();
      /* Field set is pinned by firestore.rules: exactly these keys, no
         more. `tripName` is the ENGLISH name in every language. */
      await addDoc(collection(db, "tripInterestLeads"), {
        parentName,
        childAge,
        phone:            phone.value,
        email:            email.value,
        preferredContact: cleanField(form.preferredContact, 30),
        message:          cleanField(form.message, 2000),
        tripId:           trip?.id || null,
        tripName:         resolveEn(trip?.name) || null,
        createdAt:        serverTimestamp(),
      });
      track("lead_submit", { form: "trip_interest", trip: resolveEn(trip?.name) || "" });
      onDone();
    } catch {
      setStatus("idle");
      onError(t({
        en: "We couldn't submit your interest. Please try again.",
        ar: "لم نتمكن من إرسال طلبك. حاول مرة أخرى.",
        ru: "Не удалось отправить заявку. Попробуйте снова.",
      }));
    }
  };

  return (
    <form className="trm-content trm-form" onSubmit={submit}>
      <button type="button" onClick={onBack} className="trm-back" aria-label={t(COPY.backLabel)}>
        {t(COPY.back)}
      </button>
      <p className="trm-eyebrow">
        {t({ en: "Register interest", ar: "تسجيل الاهتمام", ru: "Заявка на участие" })}
      </p>
      <h2 className="trm-title">
        {t({
          en: "Tell us about your child.",
          ar: "أخبرنا عن طفلك.",
          ru: "Расскажите о вашем ребёнке.",
        })}
      </h2>
      <p className="trm-sub">
        {t({
          en: "Our team will reach out with full camp details, pricing, availability, and next steps.",
          ar: "سيتواصل معك فريقنا بتفاصيل المخيم كاملة، والسعر، والأماكن المتاحة، والخطوات التالية.",
          ru: "Наша команда свяжется с вами: полные детали лагеря, цена, наличие мест и следующие шаги.",
        })}
      </p>

      {/* Honeypot — off-screen via .hp-field, ignored by users, filled
          by naive bots so we silently drop their submissions. Bot bait,
          never shown or announced, so it stays English on purpose. */}
      <div className="hp-field" aria-hidden="true">
        <label htmlFor="ri-website">Website</label>
        <input id="ri-website" type="text" name="website" tabIndex={-1} autoComplete="off"
          value={form.website} onChange={set("website")} />
      </div>

      <div className="trm-grid">
        <Field label={t(COPY.parentName)} required>
          <input style={inputStyle} onFocus={focus} onBlur={blur} maxLength={80} value={form.parentName} onChange={set("parentName")} required />
        </Field>
        <Field label={t(COPY.childAge)} required>
          <input style={inputStyle} onFocus={focus} onBlur={blur} value={form.childAge} onChange={set("childAge")} type="number" min="3" max="22" dir="ltr" required />
        </Field>
        <Field label={t(COPY.phone)} required>
          <input style={inputStyle} onFocus={focus} onBlur={blur} maxLength={40} value={form.phone} onChange={set("phone")} type="tel" dir="ltr" required />
        </Field>
        <Field label={t(COPY.email)} required>
          <input style={inputStyle} onFocus={focus} onBlur={blur} maxLength={200} value={form.email} onChange={set("email")} type="email" dir="ltr" required />
        </Field>
        <Field label={t(COPY.contactPref)} full>
          <RadioRow name="info-contact" value={form.preferredContact} onChange={(v) => setForm((f) => ({ ...f, preferredContact: v }))} />
        </Field>
        <Field label={t(COPY.messageField)} full>
          <textarea
            style={{ ...inputStyle, minHeight: 100, resize: "vertical" }}
            onFocus={focus} onBlur={blur}
            maxLength={2000}
            value={form.message} onChange={set("message")}
            placeholder={t({
              en: "Anything specific you'd like us to address.",
              ar: "أي تفاصيل تريد أن نغطيها.",
              ru: "Что-то конкретное, о чём нам стоит рассказать.",
            })}
          />
        </Field>
      </div>

      <button type="submit" className="btn btn-gold-lg trm-submit" disabled={status === "saving"}>
        {status === "saving"
          ? t({ en: "Submitting…", ar: "جارٍ الإرسال…", ru: "Отправляем…" })
          : t({ en: "Submit Interest", ar: "إرسال الطلب", ru: "Отправить заявку" })}
      </button>
    </form>
  );
}

/* ─── Option 2 — Pay Refundable Deposit ──────────────────── */
function DepositForm({
  trip, baseFee, currency, defaultTrack = "",
  onBack, onDone, onError,
}) {
  const { t, isRtl } = useLocale();
  const tripL = useL(trip);
  const [form, setForm] = useState({
    parentName: "",
    childName: "",
    childAge: "",
    phone: "",
    email: "",
    preferredContact: "WhatsApp",
    ackReserved: false,
    website: "",
  });
  const [status, setStatus] = useState("idle");

  /* Optional Elite Season Prep specialization track.
     "" = standard trip (no specialization), "football" or "jiujitsu" =
     +AED 1,000 (or whatever trip.eliteSeasonPrep.pricePerTrackAed says).
     The total fee + deposit + balance recompute live from this selection. */
  const esp  = trip?.eliteSeasonPrep;    /* raw record → payload values */
  const espL = tripL?.eliteSeasonPrep;   /* resolved    → visible copy  */
  const trackPrice = esp?.pricePerTrackAed || 0;
  const [selectedTrack, setSelectedTrack] = useState(defaultTrack || "");
  const trackUpcharge = selectedTrack ? trackPrice : 0;
  const totalFee      = baseFee + trackUpcharge;
  const depositAmount = Math.round(totalFee / 2);
  const balanceAmount = totalFee - depositAmount;
  /* Two versions of the same label: the English one goes to Firestore
     and the payment provider, the resolved one is displayed. */
  const trackRecord  = esp?.tracks?.find?.((track) => track.id === selectedTrack) || null;
  const trackLabelEn = trackRecord ? resolveEn(trackRecord.label) : null;
  const trackLabel   = trackRecord ? t(trackRecord.label) : null;
  const tripNameEn   = resolveEn(trip?.name);
  const tripName     = tripL?.name;

  const set    = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setBool = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.checked }));

  const canSubmit =
    form.parentName && form.phone && form.email &&
    form.childName  && form.childAge &&
    form.ackReserved && status !== "saving";

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    if (isHoneypotTripped(form.website)) { onDone(); return; }
    const parentName = cleanField(form.parentName, 80);
    const childName  = cleanField(form.childName, 80);
    const childAge   = cleanField(form.childAge, 40);
    const phone      = cleanPhone(form.phone);
    const email      = cleanEmail(form.email);
    if (!parentName || !childName || !childAge || !phone.valid || !email.valid) {
      onError(t(COPY.checkDetails));
      return;
    }
    setStatus("saving");
    const { db, collection, addDoc, serverTimestamp } = await getFirestoreApi();

    /* Field set and value shapes are pinned by firestore.rules. Every
       trip-derived string here is the English one, in every language. */
    const payload = {
      parentName,
      childName,
      childAge,
      phone:            phone.value,
      email:            email.value,
      preferredContact: cleanField(form.preferredContact, 30),
      ackReserved:      !!form.ackReserved,
      tripId:           trip?.id || null,
      tripName:         tripNameEn || null,
      baseFee,
      selectedTrack:    selectedTrack || "standard",
      trackLabel:       trackLabelEn || "Standard Trip",
      trackUpcharge,
      totalFee,
      currency,
      depositAmount,
      balanceAmount,
      paymentStatus:    "pending",
      createdAt:        serverTimestamp(),
    };

    /* ─────────────────────────────────────────────────────────
       PAYMENT PROVIDER INTEGRATION

       Replace the `runPayment` stub below with your real provider.

         Stripe        → create a PaymentIntent on a backend, then
                         use stripe.confirmCardPayment(clientSecret)
         Telr          → POST to /api/telr/order, then redirect to
                         the returned order URL
         PayTabs       → POST to /api/paytabs/pay_page, then redirect
         Checkout.com  → use Frames or call /payments with a token
         Network Int'l → call /n-genius/orders, then redirect

       The payload above carries everything the provider needs
       (amount, currency, customer info, trip id). On a successful
       payment, save with paymentStatus: "paid" and the provider's
       reference id.

       The description and metadata stay English whatever the
       visitor's language: a human reads them in the provider
       dashboard.
       ───────────────────────────────────────────────────────── */
    try {
      const ok = await runPayment({
        amount: depositAmount,
        currency,
        description: `Deposit · ${tripNameEn || "Kidsology camp"}${trackLabelEn ? ` · ${trackLabelEn}` : ""}`,
        customer: { name: form.parentName, email: form.email, phone: form.phone },
        metadata: { tripId: trip?.id || null, selectedTrack: selectedTrack || "standard" },
      });
      if (!ok) {
        setStatus("idle");
        onError(t(COPY.paymentFailed));
        return;
      }
      await addDoc(collection(db, "tripDepositRegistrations"), {
        ...payload,
        paymentStatus: "paid",
      });
      track("lead_submit", { form: "trip_deposit" });
      onDone();
    } catch {
      setStatus("idle");
      onError(t(COPY.paymentFailed));
    }
  };

  const standardTrip = t({ en: "Standard Trip", ar: "الرحلة الأساسية", ru: "Базовая поездка" });

  /* An <option> cannot hold <bdi>, so in Arabic the price carries the
     Unicode isolate pair instead. Left to right languages get the
     plain string, byte for byte as before. */
  const optionMoney = (amount) => {
    const s = formatMoney(amount, currency);
    return isRtl ? isolate(s) : s;
  };

  return (
    <form className="trm-content trm-form" onSubmit={submit}>
      <button type="button" onClick={onBack} className="trm-back" aria-label={t(COPY.backLabel)}>
        {t(COPY.back)}
      </button>
      <p className="trm-eyebrow">
        {t({
          en: "Pay refundable deposit",
          ar: "دفع دفعة مستردة",
          ru: "Возвращаемый депозит",
        })}
      </p>
      <h2 className="trm-title">
        {t({
          en: "Reserve your child's place.",
          ar: "احفظ مقعد طفلك.",
          ru: "Забронируйте место для ребёнка.",
        })}
      </h2>

      <div className="hp-field" aria-hidden="true">
        <label htmlFor="dep-website">Website</label>
        <input id="dep-website" type="text" name="website" tabIndex={-1} autoComplete="off"
          value={form.website} onChange={set("website")} />
      </div>

      <div className="trm-grid">
        <Field label={t(COPY.parentName)} required>
          <input style={inputStyle} onFocus={focus} onBlur={blur} maxLength={80} value={form.parentName} onChange={set("parentName")} required />
        </Field>
        <Field label={t(COPY.phone)} required>
          <input style={inputStyle} onFocus={focus} onBlur={blur} maxLength={40} value={form.phone} onChange={set("phone")} type="tel" dir="ltr" required />
        </Field>
        <Field label={t(COPY.email)} required full>
          <input style={inputStyle} onFocus={focus} onBlur={blur} maxLength={200} value={form.email} onChange={set("email")} type="email" dir="ltr" required />
        </Field>
        <Field label={t(COPY.contactPref)} full>
          <RadioRow name="dep-contact" value={form.preferredContact} onChange={(v) => setForm((f) => ({ ...f, preferredContact: v }))} />
        </Field>
        <Field label={t(COPY.childName)} required>
          <input style={inputStyle} onFocus={focus} onBlur={blur} maxLength={80} value={form.childName} onChange={set("childName")} required />
        </Field>
        <Field label={t(COPY.childAge)} required>
          <input style={inputStyle} onFocus={focus} onBlur={blur} value={form.childAge} onChange={set("childAge")} type="number" min="3" max="22" dir="ltr" required />
        </Field>

        {/* Optional Elite Season Prep specialization. Standard trip is
            the default. Picking a track adds +AED 1,000 to the total. */}
        {esp?.tracks?.length > 0 && (
          <Field
            label={t({
              en: "Specialization (optional)",
              ar: "التخصص (اختياري)",
              ru: "Специализация (необязательно)",
            })}
            full
          >
            <select
              style={inputStyle}
              value={selectedTrack}
              onChange={(e) => setSelectedTrack(e.target.value)}
              onFocus={focus}
              onBlur={blur}
            >
              <option value="">
                {standardTrip} · {optionMoney(baseFee)}
              </option>
              {(espL?.tracks || []).map((track, i) => (
                <option key={track.id || i} value={track.id}>
                  + {track.label} (+{optionMoney(trackPrice)})
                </option>
              ))}
            </select>
          </Field>
        )}
      </div>

      <div className="trm-fees">
        <div className="trm-fee-row">
          <span>{t({ en: "Trip selected", ar: "الرحلة المختارة", ru: "Выбранная поездка" })}</span>
          <strong>
            {tripName}
            {trackLabel ? ` · ${trackLabel}` : ""}
          </strong>
        </div>
        {selectedTrack && (
          <>
            <div className="trm-fee-row">
              <span>
                {t({
                  en: "Base trip fee",
                  ar: "سعر الرحلة الأساسي",
                  ru: "Базовая стоимость поездки",
                })}
              </span>
              <strong><bdi>{formatMoney(baseFee, currency)}</bdi></strong>
            </div>
            <div className="trm-fee-row">
              <span>{trackLabel}</span>
              <strong>+ <bdi>{formatMoney(trackUpcharge, currency)}</bdi></strong>
            </div>
          </>
        )}
        <div className="trm-fee-row">
          <span>
            {t({
              en: "Total camp fee",
              ar: "إجمالي رسوم المخيم",
              ru: "Полная стоимость лагеря",
            })}
          </span>
          <strong><bdi>{formatMoney(totalFee, currency)}</bdi></strong>
        </div>
        <div className="trm-fee-row trm-fee-row--accent">
          <span>
            {t({ en: "Deposit due now", ar: "المطلوب دفعه الآن", ru: "К оплате сейчас" })}
          </span>
          <strong><bdi>{formatMoney(depositAmount, currency)}</bdi></strong>
        </div>
        <div className="trm-fee-row">
          <span>
            {t({ en: "Remaining balance", ar: "المبلغ المتبقي", ru: "Остаток к оплате" })}
          </span>
          <strong><bdi>{formatMoney(balanceAmount, currency)}</bdi></strong>
        </div>
      </div>

      <label className="trm-check">
        <input type="checkbox" checked={form.ackReserved} onChange={setBool("ackReserved")} required />
        <span>
          {t({
            en: "I understand that my child's spot is reserved after the refundable deposit payment is completed, and that the Kidsology team will contact me to complete the next steps.",
            ar: "أفهم أن مقعد طفلي يُحفظ بعد إتمام دفع الدفعة المستردة، وأن فريق Kidsology سيتواصل معي لإكمال الخطوات التالية.",
            ru: "Я понимаю, что место для моего ребёнка бронируется после оплаты возвращаемого депозита и что команда Kidsology свяжется со мной для следующих шагов.",
          })}
        </span>
      </label>

      <button type="submit" className="btn btn-gold-lg trm-submit" disabled={!canSubmit}>
        {status === "saving"
          ? t({
              en: "Processing payment…",
              ar: "جارٍ تنفيذ الدفع…",
              ru: "Обрабатываем платёж…",
            })
          : (
            <>
              {t({ en: "Pay Deposit", ar: "ادفع الدفعة", ru: "Внести депозит" })}
              {" · "}
              <bdi>{formatMoney(depositAmount, currency)}</bdi>
            </>
          )}
      </button>
    </form>
  );
}

/* ─── Dubai registration form ─────────────────────────────── */
function EarlyAccessForm({ trip, onDone, onError }) {
  const { t } = useLocale();
  const [form, setForm] = useState({
    parentName: "",
    childAge: "",
    phone: "",
    email: "",
    preferredContact: "WhatsApp",
    message: "",
    website: "",
  });
  const [status, setStatus] = useState("idle");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (isHoneypotTripped(form.website)) { onDone(); return; }
    const parentName = cleanField(form.parentName, 80);
    const childAge   = cleanField(form.childAge, 40);
    const phone      = cleanPhone(form.phone);
    const email      = cleanEmail(form.email);
    if (!parentName || !childAge || !phone.valid || !email.valid) {
      onError(t(COPY.checkDetails));
      return;
    }
    setStatus("saving");
    try {
      const { db, collection, addDoc, serverTimestamp } = await getFirestoreApi();
      /* Field set pinned by firestore.rules. `tripName` stays English. */
      await addDoc(collection(db, "earlyAccessLeads"), {
        parentName,
        childAge,
        phone:            phone.value,
        email:            email.value,
        preferredContact: cleanField(form.preferredContact, 30),
        message:          cleanField(form.message, 2000),
        tripId:           trip?.id || null,
        tripName:         resolveEn(trip?.name) || null,
        createdAt:        serverTimestamp(),
      });
      track("lead_submit", { form: "early_access", trip: resolveEn(trip?.name) || "" });
      onDone();
    } catch {
      setStatus("idle");
      onError(t({
        en: "We couldn't add you to the list. Please try again.",
        ar: "لم نتمكن من إضافتك إلى القائمة. حاول مرة أخرى.",
        ru: "Не удалось добавить вас в список. Попробуйте снова.",
      }));
    }
  };

  return (
    <form className="trm-content trm-form" onSubmit={submit}>
      <p className="trm-eyebrow">
        {t({
          en: "Dubai Winter Development Camp",
          ar: "مخيم دبي الشتوي للتطوير",
          ru: "Зимний развивающий лагерь в Дубае",
        })}
      </p>
      <h2 className="trm-title">
        {t({
          en: "Register for the Dubai Camp",
          ar: "سجّل في مخيم دبي",
          ru: "Регистрация в лагерь в Дубае",
        })}
      </h2>
      <p className="trm-sub">
        {t({
          en: "Leave your details and the Kidsology team will contact you with the full camp information and to confirm a place for your child.",
          ar: "اترك بياناتك وسيتواصل معك فريق Kidsology بمعلومات المخيم كاملة ولتأكيد مقعد طفلك.",
          ru: "Оставьте свои данные, и команда Kidsology свяжется с вами: полная информация о лагере и подтверждение места для вашего ребёнка.",
        })}
      </p>

      <div className="hp-field" aria-hidden="true">
        <label htmlFor="ea-website">Website</label>
        <input id="ea-website" type="text" name="website" tabIndex={-1} autoComplete="off"
          value={form.website} onChange={set("website")} />
      </div>

      <div className="trm-grid">
        <Field label={t(COPY.parentName)} required>
          <input style={inputStyle} onFocus={focus} onBlur={blur} maxLength={80} value={form.parentName} onChange={set("parentName")} required />
        </Field>
        <Field label={t(COPY.childAge)} required>
          <input style={inputStyle} onFocus={focus} onBlur={blur} value={form.childAge} onChange={set("childAge")} type="number" min="3" max="22" dir="ltr" required />
        </Field>
        <Field label={t(COPY.phone)} required>
          <input style={inputStyle} onFocus={focus} onBlur={blur} maxLength={40} value={form.phone} onChange={set("phone")} type="tel" dir="ltr" required />
        </Field>
        <Field label={t(COPY.email)} required>
          <input style={inputStyle} onFocus={focus} onBlur={blur} maxLength={200} value={form.email} onChange={set("email")} type="email" dir="ltr" required />
        </Field>
        <Field label={t(COPY.contactPref)} full>
          <RadioRow name="early-contact" value={form.preferredContact} onChange={(v) => setForm((f) => ({ ...f, preferredContact: v }))} />
        </Field>
        <Field label={t(COPY.messageField)} full>
          <textarea
            style={{ ...inputStyle, minHeight: 100, resize: "vertical" }}
            onFocus={focus} onBlur={blur}
            maxLength={2000}
            value={form.message} onChange={set("message")}
          />
        </Field>
      </div>

      <button type="submit" className="btn btn-gold-lg trm-submit" disabled={status === "saving"}>
        {status === "saving"
          ? t({ en: "Saving…", ar: "جارٍ الحفظ…", ru: "Сохраняем…" })
          : t({ en: "Register Now", ar: "سجّل الآن", ru: "Зарегистрироваться" })}
      </button>
    </form>
  );
}

/* ─── Confirmation / Error views ──────────────────────────── */
function Confirmation({ title, body, onClose }) {
  const { t } = useLocale();
  return (
    <div className="trm-content trm-confirm">
      <div className="trm-confirm-icon" aria-hidden="true">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="20 6 9 17 4 12" />
        </svg>
      </div>
      <h2 className="trm-title">{title}</h2>
      <p className="trm-sub">{body}</p>
      <button onClick={onClose} className="btn btn-gold-lg trm-submit">{t(COPY.close)}</button>
    </div>
  );
}

function ErrorView({ message, onRetry, onClose }) {
  const { t } = useLocale();
  return (
    <div className="trm-content trm-confirm">
      <div className="trm-confirm-icon trm-confirm-icon--error" aria-hidden="true">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
          <line x1="12" y1="8" x2="12" y2="13" />
          <circle cx="12" cy="17" r="0.8" fill="currentColor" />
          <circle cx="12" cy="12" r="10" />
        </svg>
      </div>
      <h2 className="trm-title">
        {t({ en: "Something went wrong.", ar: "حدث خطأ ما.", ru: "Что-то пошло не так." })}
      </h2>
      <p className="trm-sub">{message}</p>
      <div className="trm-actions">
        <button onClick={onRetry} className="btn btn-gold-lg trm-submit">{t(COPY.tryAgain)}</button>
        <button onClick={onClose} className="trm-back" type="button">{t(COPY.close)}</button>
      </div>
    </div>
  );
}

/* ─── Helpers ─────────────────────────────────────────────── */
function Field({ label, required, full, children }) {
  return (
    <div className={`trm-field${full ? " trm-field--full" : ""}`}>
      <label style={labelStyle}>
        {label}
        {required ? <span style={{ color: "var(--gold-dim)" }}> *</span> : null}
      </label>
      {children}
    </div>
  );
}

function RadioRow({ name, value, onChange }) {
  const { t } = useLocale();
  return (
    <div className="trm-radio-row">
      {CONTACT_OPTIONS.map((c) => (
        <label key={c.value} className="trm-radio">
          <input
            type="radio"
            name={name}
            checked={value === c.value}
            onChange={() => onChange(c.value)}
          />
          <span>{t(c.label)}</span>
        </label>
      ))}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   Payment stub
   Returns Promise<boolean> resolving true on payment success.
   Replace this with your real provider integration (see the
   commented block in DepositForm submit). For now it logs the
   payload and resolves true so the form-flow can be tested
   end-to-end. Replace once a real provider is wired.
   ─────────────────────────────────────────────────────────────*/
async function runPayment(payload) {
  if (typeof window !== "undefined") {
    // eslint-disable-next-line no-console
    console.info("[Kidsology] runPayment stub — payload:", payload);
  }
  // TODO: replace with provider integration. Returning true keeps
  // the lead in Firestore and shows the success confirmation.
  return true;
}
